import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiSend, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiExternalLink, FiEye, FiEyeOff, FiUpload, FiX, FiImage } from 'react-icons/fi';
import deploymentConfig from '../config/deployment';
import toast from 'react-hot-toast';
import { settingsAPI } from '../services/api';

// Helper function to mask wallet addresses (show first 3 and last 3 characters)
const maskWalletAddress = (address) => {
  if (!address) return 'N/A';
  if (address.length <= 6) return address;
  return `${address.substring(0, 3)}${'•'.repeat(Math.min(address.length - 6, 40))}${address.substring(address.length - 3)}`;
};

const NFTMintingTest = () => {
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [achievementTitle, setAchievementTitle] = useState('Test Achievement');
  const [achievementPoints, setAchievementPoints] = useState(10);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [addressValid, setAddressValid] = useState(null);
  const [masterWalletVisible, setMasterWalletVisible] = useState(false);
  const [recipientAddressVisible, setRecipientAddressVisible] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch blockchain settings to get wallet address
  const { data: settings } = useQuery(
    'cms-settings',
    () => settingsAPI.getSettings(),
    {
      select: (data) => data.data.settings
    }
  );

  // Fetch blockchain network status (includes balance from backend - multi-chain aware)
  const { data: networkStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery(
    'blockchain-status',
    () => fetch(deploymentConfig.getApiUrl('nft-minting/status'), {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}` }
    }).then(res => res.json()),
    { refetchInterval: 30000 }
  );

  // Fetch available templates for minting
  const { data: templatesData, isLoading: templatesLoading } = useQuery(
    'minting-templates',
    () => fetch(deploymentConfig.getApiUrl('nft-minting/templates'), {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}` }
    }).then(res => res.json())
  );

  // NFT minting mutation
  const mintNFTMutation = useMutation(
    (mintData) => fetch(deploymentConfig.getApiUrl('nft-minting/mint-test'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}`
      },
      body: JSON.stringify(mintData)
    }).then(res => res.json()),
    {
      onSuccess: (data) => {
        if (data.result) {
          toast.success('NFT minted successfully!');
          // Clear form and reset to master wallet address
          setRecipientAddress(networkStatus?.masterWallet?.address || '');
          setAchievementTitle('Test Achievement');
          setAchievementPoints(10);
          setAddressValid(null);
        } else {
          toast.error(data.message || 'Minting failed');
        }
      },
      onError: (error) => {
        toast.error('Failed to mint NFT');
        console.error('Minting error:', error);
      }
    }
  );

  // Address validation
  const validateAddress = async () => {
    if (!recipientAddress.trim()) {
      setAddressValid(null);
      return;
    }

    setIsValidatingAddress(true);
    try {
      const response = await fetch(deploymentConfig.getApiUrl('nft-minting/validate-address'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}`
        },
        body: JSON.stringify({ address: recipientAddress })
      });
      
      const data = await response.json();
      setAddressValid(data.valid);
    } catch (error) {
      console.error('Address validation error:', error);
      setAddressValid(false);
    }
    setIsValidatingAddress(false);
  };

  // Balance is now fetched by the backend /status endpoint (multi-chain aware)
  // No need for client-side balance fetching

  // Pre-populate recipient address with master wallet from status (multi-chain aware)
  useEffect(() => {
    if (networkStatus?.masterWallet?.address && !recipientAddress) {
      setRecipientAddress(networkStatus.masterWallet.address);
    }
  }, [networkStatus?.masterWallet?.address, recipientAddress]);

  // Auto-validate address when it changes (including pre-populated address)
  useEffect(() => {
    if (recipientAddress && networkStatus?.masterWallet?.address &&
        recipientAddress === networkStatus.masterWallet.address) {
      // Auto-validate main wallet address
      setTimeout(() => {
        validateAddress();
      }, 200);
    }
  }, [recipientAddress, networkStatus?.masterWallet?.address]);

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only image files (JPEG, PNG, GIF, WebP) are allowed');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(deploymentConfig.getApiUrl('nft-minting/upload-test-image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('cms_accessToken')}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setCustomImage(data.imageUrl);
        toast.success('Image uploaded successfully');
      } else {
        toast.error(data.message || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear uploaded image
  const clearCustomImage = () => {
    setCustomImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMintNFT = () => {
    if (!selectedTemplateId || !recipientAddress || !addressValid) {
      toast.error('Please fill all required fields with valid data');
      return;
    }

    const mintData = {
      templateId: selectedTemplateId,
      recipientAddress: recipientAddress,
      achievementData: {
        title: achievementTitle,
        points: parseInt(achievementPoints),
        type: 'test',
        difficulty: 'normal'
      }
    };

    // Add custom image if uploaded
    if (customImage) {
      mintData.customImage = customImage;
    }

    mintNFTMutation.mutate(mintData);
  };

  const selectedTemplate = templatesData?.templates?.find(t => t.id === selectedTemplateId);

  return (
    <div className="space-y-6">
      {/* Network Status Card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Blockchain Network Status</h3>
          <button
            onClick={() => refetchStatus()}
            disabled={statusLoading}
            className="btn btn-secondary btn-sm flex items-center space-x-2"
          >
            <FiRefreshCw className={`w-4 h-4 ${statusLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {statusLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="bg-gray-200 h-4 rounded w-3/4"></div>
            <div className="bg-gray-200 h-4 rounded w-1/2"></div>
          </div>
        ) : networkStatus?.connected ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="w-4 h-4 text-green-500" />
                <span className="font-medium">Connected</span>
              </div>
              <div><strong>Blockchain:</strong> {networkStatus.blockchain}</div>
              <div><strong>Network:</strong> {networkStatus.network}</div>
              <div><strong>Last Round:</strong> {networkStatus.lastRound?.toLocaleString()}</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <strong>Master Wallet:</strong>
                <code className="font-mono text-sm">
                  {masterWalletVisible
                    ? networkStatus.masterWallet?.address
                    : maskWalletAddress(networkStatus.masterWallet?.address)
                  }
                </code>
                <button
                  type="button"
                  onClick={() => setMasterWalletVisible(!masterWalletVisible)}
                  className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  title={masterWalletVisible ? "Hide address" : "Show address"}
                >
                  {masterWalletVisible ? (
                    <FiEyeOff className="w-4 h-4" />
                  ) : (
                    <FiEye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div>
                <strong>Balance:</strong>
                <span className="ml-2 font-mono text-green-600">
                  {networkStatus.masterWallet?.balance || '0'} {networkStatus.masterWallet?.symbol || networkStatus.nativeCurrency || ''}
                </span>
              </div>
              <div>
                <strong>Avg Cost:</strong>
                <span className="ml-2 font-mono">{networkStatus.avgCost || 'unknown'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-red-600">
            <FiAlertCircle className="w-4 h-4" />
            <span>Blockchain connection failed</span>
          </div>
        )}
      </div>

      {/* NFT Minting Test Form */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Test NFT Minting</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-4">
            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                NFT Template *
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a template...</option>
                {templatesData?.templates?.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.genre} - {template.rarity})
                  </option>
                ))}
              </select>
              {templatesLoading && (
                <p className="text-sm text-gray-500 mt-1">Loading templates...</p>
              )}
            </div>

            {/* Recipient Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Wallet Address *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={recipientAddressVisible ? recipientAddress : maskWalletAddress(recipientAddress)}
                  onChange={(e) => {
                    // Only allow changes when visible
                    if (recipientAddressVisible) {
                      setRecipientAddress(e.target.value);
                      setAddressValid(null);
                    }
                  }}
                  onFocus={() => setRecipientAddressVisible(true)}
                  onBlur={() => {
                    validateAddress();
                    setRecipientAddressVisible(false);
                  }}
                  placeholder="Enter recipient address (defaults to main wallet)"
                  className={`w-full px-3 py-2 pr-20 border rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm ${
                    addressValid === true ? 'border-green-500' :
                    addressValid === false ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <div className="absolute right-3 top-3 flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setRecipientAddressVisible(!recipientAddressVisible)}
                    className="p-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                    title={recipientAddressVisible ? "Hide address" : "Show address"}
                  >
                    {recipientAddressVisible ? (
                      <FiEyeOff className="w-4 h-4" />
                    ) : (
                      <FiEye className="w-4 h-4" />
                    )}
                  </button>
                  {isValidatingAddress ? (
                    <FiRefreshCw className="w-4 h-4 animate-spin text-gray-400" />
                  ) : addressValid === true ? (
                    <FiCheckCircle className="w-4 h-4 text-green-500" />
                  ) : addressValid === false ? (
                    <FiAlertCircle className="w-4 h-4 text-red-500" />
                  ) : null}
                </div>
              </div>
              {addressValid === false && (
                <p className="text-sm text-red-600 mt-1">Invalid {networkStatus?.blockchain || ''} wallet address format</p>
              )}
              {!addressValid && recipientAddress && networkStatus?.masterWallet?.address &&
               recipientAddress === networkStatus.masterWallet.address && (
                <p className="text-sm text-primary-600 mt-1">Using master wallet address as recipient</p>
              )}
            </div>

            {/* Achievement Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Achievement Title
                </label>
                <input
                  type="text"
                  value={achievementTitle}
                  onChange={(e) => setAchievementTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Points
                </label>
                <input
                  type="number"
                  value={achievementPoints}
                  onChange={(e) => setAchievementPoints(e.target.value)}
                  min="1"
                  max="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Custom Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Image (Optional)
              </label>
              <div className="space-y-2">
                {customImage ? (
                  <div className="relative">
                    <div className="border border-gray-300 rounded-md p-2 bg-gray-50">
                      <div className="flex items-center space-x-3">
                        <img
                          src={`${deploymentConfig.apiBaseUrl.replace('/api', '')}${customImage}`}
                          alt="Custom NFT"
                          className="w-16 h-16 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Custom image uploaded</p>
                          <p className="text-xs text-gray-500 truncate">{customImage}</p>
                        </div>
                        <button
                          onClick={clearCustomImage}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Remove image"
                        >
                          <FiX className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      <FiCheckCircle className="inline w-3 h-3 mr-1" />
                      This image will be used instead of the template design
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="nft-test-image-upload"
                    />
                    <label
                      htmlFor="nft-test-image-upload"
                      className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors ${
                        isUploading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isUploading ? (
                        <>
                          <FiRefreshCw className="w-5 h-5 mr-2 text-primary-500 animate-spin" />
                          <span className="text-sm text-gray-600">Uploading...</span>
                        </>
                      ) : (
                        <>
                          <FiUpload className="w-5 h-5 mr-2 text-gray-400" />
                          <span className="text-sm text-gray-600">Upload custom image</span>
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      JPEG, PNG, GIF, WebP up to 10MB. Overrides template image.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Mint Button */}
            <button
              onClick={handleMintNFT}
              disabled={!selectedTemplateId || !recipientAddress || addressValid !== true || mintNFTMutation.isLoading}
              className="w-full btn btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mintNFTMutation.isLoading ? (
                <>
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  <span>Minting NFT...</span>
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  <span>Mint Test NFT</span>
                </>
              )}
            </button>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">NFT Preview</h4>

            {selectedTemplate ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Image Preview - Custom image takes priority */}
                {customImage ? (
                  <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                    <img
                      src={`${deploymentConfig.apiBaseUrl.replace('/api', '')}${customImage}`}
                      alt="Custom NFT"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                      Custom Image
                    </div>
                  </div>
                ) : (selectedTemplate.custom_design_url || selectedTemplate.custom_icon_url || selectedTemplate.custom_background_url) ? (
                  <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                    <img
                      src={`${deploymentConfig.apiBaseUrl.replace('/api', '')}${selectedTemplate.custom_design_url ||
                        selectedTemplate.custom_icon_url ||
                        selectedTemplate.custom_background_url}`}
                      alt={selectedTemplate.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="w-full flex items-center justify-center text-white"
                    style={{
                      background: selectedTemplate.background_gradient || '#4F46E5',
                      height: '200px',
                      fontSize: '4rem'
                    }}
                  >
                    {selectedTemplate.icon_emoji || '🏆'}
                  </div>
                )}

                {/* Template Info */}
                <div className="p-4 space-y-3">
                  <div>
                    <h5 className="font-medium text-lg">{selectedTemplate.name}</h5>
                    <p className="text-sm text-gray-600">{selectedTemplate.genre} • {selectedTemplate.rarity}</p>
                  </div>

                  {selectedTemplate.description && (
                    <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                  )}

                  <div className="text-xs text-gray-500 space-y-1 pt-3 border-t">
                    <div><strong>Achievement:</strong> {achievementTitle}</div>
                    <div><strong>Points:</strong> {achievementPoints}</div>
                    <div><strong>Minted:</strong> {selectedTemplate.mint_count || 0} times</div>
                    {customImage && (
                      <div className="text-green-600"><strong>Image:</strong> Custom uploaded</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500">
                Select a template to see preview
              </div>
            )}
          </div>
        </div>

        {/* Success Result */}
        {mintNFTMutation.data?.result && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div className="flex-1 space-y-2">
                <h4 className="font-medium text-green-800">NFT Minted Successfully!</h4>
                <div className="text-sm text-green-700 space-y-1">
                  <div><strong>Asset ID:</strong> {mintNFTMutation.data.result.assetId}</div>
                  <div><strong>Transaction ID:</strong> {mintNFTMutation.data.result.txnId}</div>
                  <div><strong>Recipient:</strong> {mintNFTMutation.data.result.recipient}</div>
                </div>
                <div className="flex space-x-4 mt-2">
                  {mintNFTMutation.data.result.explorerUrl && (
                    <a
                      href={mintNFTMutation.data.result.explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-800"
                    >
                      <FiExternalLink className="w-4 h-4" />
                      <span>View Transaction</span>
                    </a>
                  )}
                  {mintNFTMutation.data.result.assetExplorerUrl && (
                    <a
                      href={mintNFTMutation.data.result.assetExplorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-sm text-green-600 hover:text-green-800"
                    >
                      <FiExternalLink className="w-4 h-4" />
                      <span>View NFT Asset</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Result */}
        {mintNFTMutation.isError && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <FiAlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-800">Minting Failed</h4>
                <p className="text-sm text-red-700 mt-1">
                  {mintNFTMutation.error?.message || 'An error occurred while minting the NFT'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NFTMintingTest;